import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CircleArrowRightIcon, RocketIcon } from "lucide-react";

import { groupBannerImage, groupCoverImage } from "simplydo/core/images";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { CardContent, CardAction, CardLink } from "simplydo/react/ui/card";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import util from "utils/utils";

import type { GroupCardGroup } from "./types";

const CoverImage = styled.div<{ $coverImage: string }>`
  height: 40px;
  min-width: 40px;
  margin-right: 5px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.secondaryColour};
  background-image: ${({ $coverImage }) => `url(${$coverImage})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  &:hover {
    box-shadow: inset 0 0 0 2000px rgba(211, 211, 211, 0.3);
  }
`;

const GroupInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  max-height: 40px;
  padding-right: 5px;
  margin: 0 10px 0 0;
  width: 40%;
  h4 {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
    margin-bottom: 2px;
  }
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: 100%;
  }
`;

const GroupMeta = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin: 0;
`;

const GroupCardContainer = styled(CardLink)`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  flex: 1;
  margin: 0;

  ${CardContent} {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &,
  p,
  h4 {
    color: #000;
  }
`;

const GroupCard = ({ group }: { group: GroupCardGroup }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const groupLink = `/groups/${group._id}`;
  const hasCoverAndBanner = Boolean(group?.coverImage && group.bannerImage);

  return (
    <CardLink fluid elevation="large" to={groupLink}>
      <div
        style={{
          width: "100%",
          height: "75px",
          position: "relative",
          backgroundImage: `url(${groupBannerImage(group)})`,
          backgroundColor: theme.secondaryColour,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      />

      <CardContent style={{ position: "relative" }}>
        {hasCoverAndBanner ? (
          <>
            <div
              style={{
                width: 80,
                height: 80,
                overflow: "hidden",
                position: "absolute",
                marginTop: -80,
                boxShadow: "rgb(0 0 0 / 30%) 0.5px 2px 10px 4px",
                border: "2px solid white",
                backgroundImage: `url(${groupCoverImage(group)})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                borderRadius: 7,
              }}
            />
            <H3
              style={{
                marginBottom: group.description ? 5 : undefined,
                marginTop: 5,
                color: "#4d4949",
              }}
            >
              {group.name}
            </H3>
          </>
        ) : (
          <H3
            style={{
              display: "block",
              flex: 1,
              marginBottom: group.description ? 5 : undefined,
              color: "#4d4949",
            }}
          >
            {group.name}
          </H3>
        )}

        {group.description ? <Subheader>{group.description}</Subheader> : null}
      </CardContent>

      <CardAction
        color="primary"
        onClick={() => {
          navigate(groupLink);
        }}
      >
        {t("generic.viewType", { entity: t("generic.group") })}{" "}
        <Icon icon={CircleArrowRightIcon} />
      </CardAction>
    </CardLink>
  );
};

export const GroupCardListView = ({ group }: { group: GroupCardGroup }) => (
  <GroupCardContainer elevation="small" to={`/groups/${group._id}`}>
    <CardContent>
      <CoverImage
        $coverImage={
          group?.coverImage
            ? util.groupCoverImage(group, "300x300")
            : group?.bannerImage
              ? util.groupBannerImage(group)
              : "https://cdn.simplydo.co.uk/images/groupCoverImage.png"
        }
      />
      <GroupInfo>
        <div>
          <H4>{group.name}</H4>
        </div>
        {group.description ? (
          <div>
            <p>{group.description}</p>
          </div>
        ) : null}
      </GroupInfo>
      <GroupMeta>
        {group.isLeader ? (
          <Label size="small" color="purple">
            <Icon icon={RocketIcon} />
            Leader
          </Label>
        ) : null}
      </GroupMeta>
    </CardContent>
  </GroupCardContainer>
);

export default GroupCard;
